import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';

import BoundingBoxOverlay from './BoundingBoxOverlay';
import { DetailedEvidenceBox } from '../../types/project';

export interface BoundingBoxData {
  [pageKey: string]: number[][] | DetailedEvidenceBox[]; // Support both legacy and new formats
}

interface DocumentPageRendererProps {
  documentId: string;
  pageNumber: number;
  boundingBoxes?: BoundingBoxData;
  width?: number;
  onImageLoad?: (dimensions: { width: number; height: number }) => void;
  onBoundingBoxClick?: (box: number[], boxIndex: number) => void;
}

const DocumentPageRenderer: React.FC<DocumentPageRendererProps> = ({
  documentId,
  pageNumber,
  boundingBoxes,
  width = 800,
  onImageLoad,
  onBoundingBoxClick
}) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [pdfPageDimensions, setPdfPageDimensions] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    let currentUrl: string | null = null;

    const loadPageImage = async () => {
      setLoading(true);
      setError('');

      // Cleanup any existing URL
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
        currentUrl = null;
      }

      try {
        const response = await fetch(
          `/api/documents/${documentId}/page/${pageNumber}/image?width=${width}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Document or page not found');
          } else if (response.status === 400) {
            const errorData = await response.json();
            const detail = errorData.detail || 'Invalid request';

            // Check if this is a page validation error
            if (detail.includes('Invalid page') && detail.includes('Document has')) {
              throw new Error(`Page ${pageNumber} is not available for this document. ${detail}`);
            } else {
              throw new Error(detail);
            }
          } else {
            throw new Error('Failed to load page image');
          }
        }

        // Create blob URL from response
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        currentUrl = url;
        setImageUrl(url);

      } catch (err) {
        console.error(`Failed to load page ${pageNumber} for document ${documentId}:`, err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    if (documentId && pageNumber > 0) {
      loadPageImage();
    }

    // Cleanup blob URL when component unmounts or dependencies change
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [documentId, pageNumber, width]);

  // Fetch PDF page dimensions for coordinate system conversion
  useEffect(() => {
    const fetchPdfDimensions = async () => {
      try {
        const response = await fetch(`/api/documents/${documentId}/page/${pageNumber}/dimensions`);
        if (response.ok) {
          const dimensions = await response.json();
          setPdfPageDimensions({
            width: dimensions.width,
            height: dimensions.height
          });

          console.log('📏 PDF PAGE DIMENSIONS FETCHED:', {
            documentId,
            pageNumber,
            pdfDimensions: dimensions
          });
        } else if (response.status === 400) {
          // Page validation failed for dimensions as well
          const errorData = await response.json();
          console.warn('PDF dimensions fetch failed for invalid page:', errorData.detail);
        }
      } catch (error) {
        console.warn('Failed to fetch PDF page dimensions:', error);
      }
    };

    fetchPdfDimensions();
  }, [documentId, pageNumber]);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
    setImageDimensions(dimensions);

    console.log('📐 DISPLAY IMAGE LOADED DEBUG:', {
      documentId,
      pageNumber,
      displayImageDimensions: dimensions,
      targetWidth: width,
      imageUrl: imageUrl,
      scaleFromTarget: dimensions.width / width
    });

    if (onImageLoad) {
      onImageLoad(dimensions);
    }
  };

  const handleImageError = () => {
    setError('Failed to display page image');
    setLoading(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
          backgroundColor: 'grey.50',
          borderRadius: 1
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
          Loading page {pageNumber}...
        </Typography>
      </Box>
    );
  }

  if (error) {
    // Check if this is a page validation error
    const isPageValidationError = error.includes('Page') && error.includes('not available');

    return (
      <Alert
        severity={isPageValidationError ? "warning" : "error"}
        sx={{ m: 2 }}
      >
        <Typography variant="body2">
          <strong>
            {isPageValidationError
              ? `Page ${pageNumber} not available`
              : `Failed to load page ${pageNumber}`
            }
          </strong>
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
        {isPageValidationError && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            This usually means the evidence page number refers to a different document in the set, or the page mapping needs to be updated.
          </Typography>
        )}
      </Alert>
    );
  }

  const pageKey = `page_${pageNumber}`;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        backgroundColor: 'white',
        borderRadius: 1,
        border: 1,
        borderColor: 'grey.300',
        overflow: 'hidden',
        boxShadow: 1,
      }}
    >
      <img
        src={imageUrl}
        alt={`Page ${pageNumber}`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{
          display: 'block',
          maxWidth: '100%',
          height: 'auto'
        }}
      />

      {/* Bounding box overlay - Enhanced with documentId for duplicate page mapping handling */}
      {imageDimensions && boundingBoxes && pdfPageDimensions && (
        <BoundingBoxOverlay
          pageKey={pageKey}
          documentId={documentId}
          boundingBoxes={boundingBoxes}
          imageWidth={imageDimensions.width}
          imageHeight={imageDimensions.height}
          pdfPageWidth={pdfPageDimensions.width}
          pdfPageHeight={pdfPageDimensions.height}
          onBoxClick={onBoundingBoxClick}
        />
      )}
    </Box>
  );
};

export default DocumentPageRenderer;